import React, { useEffect, useState } from 'react';
import { ButtonWithConfirmation } from '../../../../components/ButtonWithConfirmation';
import { useDeleteOrderFulfillmentPicturesMutation } from '../../../../graphql/mutations/deleteOrderFulfillmentPictures.generated';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import {
  FaDownload,
  FaEdit,
  FaLink,
  FaSave,
  FaTrash,
  FaUnlink,
} from 'react-icons/fa';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';
import type { OrderFulfillmentPicture as OrderFulfillmentPictureFields } from '../../../../graphql/generatedTypes';
import { Link } from 'react-router-dom';
import CustomPagination from '../../../../components/CustomPagination';
import LinkOrderFulfillmentPicturesModal from './LinkOrderFulfillmentPicturesModal';
import { useUnlinkOrderFulfillmentPicturesMutation } from '../../../../graphql/mutations/unlinkOrderFulfillmentPictures.generated';
import SimpleMap from './SimpleMap';
import { useUpdateOrderFulfillmentPictureMutation } from '../../../../graphql/mutations/updateOrderFulfillmentPicture.generated';
import { downloadImage } from '@/utils/download';

type OrderFulfillmentPicture = Omit<
  OrderFulfillmentPictureFields,
  'is_uploaded'
>;

type OrderFulfillmentPicturesTableProps = {
  loading: boolean;
  error: any | undefined;
  total: number;
  pictures: OrderFulfillmentPicture[];
  loadMore?: (skip: number, take: number) => void;
  page: number;
  setPage?: (page: number) => void;
  showLinkButton: boolean;
  showUnlinkButton: boolean;
  showDeleteButton?: boolean;
  showDownload?: boolean;
  hideCheckbox?: boolean;
};

export const ORDER_FULFILLMENT_PICTURES_PAGE_SIZE = 20;

export const OrderFulfillmentPicturesTable = ({
  loading,
  error,
  total,
  pictures,
  loadMore,
  page,
  setPage,
  showLinkButton,
  showUnlinkButton,
  showDeleteButton = true,
  showDownload = false,
  hideCheckbox = false,
}: OrderFulfillmentPicturesTableProps) => {
  const [linkModalIsOpen, setLinkModalIsOpen] = React.useState(false);
  const [selectedPictureIds, setSelectedPictureIds] = React.useState<string[]>(
    [],
  );
  const [showImageFullscreen, setShowImageFullscreen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [picturesInEditMode, setPicturesInEditMode] = useState<string[]>([]);
  const [pictureUpdates, setPictureUpdates] = useState<
    OrderFulfillmentPicture[]
  >([]);

  const [deletePicturesMutation] = useDeleteOrderFulfillmentPicturesMutation();
  const [unlinkPicturesMutation] = useUnlinkOrderFulfillmentPicturesMutation();
  const [updatePictureMutation] = useUpdateOrderFulfillmentPictureMutation();

  useEffect(loadCurrentPage, [page]);

  function loadCurrentPage() {
    if (loadMore != null) {
      loadMore(
        (page - 1) * ORDER_FULFILLMENT_PICTURES_PAGE_SIZE,
        ORDER_FULFILLMENT_PICTURES_PAGE_SIZE,
      );
    }
  }

  function handleCheckboxChange(pictureId: string) {
    setSelectedPictureIds((prevSelectedRows) =>
      prevSelectedRows.includes(pictureId)
        ? prevSelectedRows.filter((id) => id !== pictureId)
        : [...prevSelectedRows, pictureId],
    );
  }

  function deletePictures() {
    if (selectedPictureIds.length === 0) {
      return;
    }

    deletePicturesMutation({
      variables: {
        picture_ids: selectedPictureIds,
      },
      refetchQueries: [
        'GetUnlinkedOrderFulfillmentPictures',
        'OrderFulfillmentInternal',
      ],
    });
    setSelectedPictureIds([]);
  }

  function unlinkPictures() {
    if (selectedPictureIds.length === 0) {
      return;
    }

    unlinkPicturesMutation({
      variables: {
        picture_ids: selectedPictureIds,
      },
      refetchQueries: ['OrderFulfillmentInternal'],
    });
    setSelectedPictureIds([]);
  }

  function onSave(picture: OrderFulfillmentPicture) {
    setPicturesInEditMode((prev) => [
      ...prev.filter((id) => id !== picture.id),
    ]);
    updatePictureMutation({
      variables: {
        picture_id: picture.id,
        update: {
          comment: picture.comment,
        },
      },
      refetchQueries: [
        'GetUnlinkedOrderFulfillmentPictures',
        'OrderFulfillmentInternal',
      ],
    });
  }

  function getAddressString(picture: OrderFulfillmentPicture): string {
    const street = picture.street ?? '';
    const city = picture.city ?? '';
    const postalCode = picture.postal_code ?? '';
    const countryCode =
      picture.country_code != null && picture.country_code.length === 2
        ? i18n.t(`Common:Country:${picture.country_code}`)
        : '';

    return [street, `${postalCode} ${city}`, countryCode]
      .filter(Boolean)
      .join(', ');
  }

  let pageCount = Math.ceil(total / ORDER_FULFILLMENT_PICTURES_PAGE_SIZE);
  if (setPage == null) {
    pageCount = 1;
  }
  return (
    <div>
      <div>
        {showDeleteButton && (
          <ButtonWithConfirmation
            color="danger"
            confirmationText={i18n.t('OrderFulfillmentPictures:deleteMessage', {
              amount: selectedPictureIds.length,
            })}
            disabled={selectedPictureIds.length === 0}
            onConfirmed={deletePictures}
            className="float-end ms-1"
            confirmationHeaderText={i18n.t('OrderFulfillmentPictures:delete')}
          >
            <FaTrash className="me-1" />{' '}
            {i18n.t('OrderFulfillmentPictures:delete')}
          </ButtonWithConfirmation>
        )}

        {showLinkButton && (
          <Button
            disabled={selectedPictureIds.length === 0}
            className="float-end ms-1"
            onClick={() => setLinkModalIsOpen(true)}
          >
            <FaLink className="me-1" />{' '}
            {i18n.t('OrderFulfillmentPictures:link')}
          </Button>
        )}
        {showUnlinkButton && (
          <Button
            disabled={selectedPictureIds.length === 0}
            className="float-end ms-1"
            onClick={unlinkPictures}
          >
            <FaUnlink className="me-1" />{' '}
            {i18n.t('OrderFulfillmentPictures:unlink')}
          </Button>
        )}
      </div>
      <br style={{ clear: 'both' }} />
      <Table className="mt-2">
        <thead>
          <tr>
            {!hideCheckbox && (
              <th>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Input
                    className="form-control"
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPictureIds(
                          pictures.map((picture) => picture.id),
                        );
                      } else {
                        setSelectedPictureIds([]);
                      }
                    }}
                    checked={selectedPictureIds.length === pictures.length}
                  />
                </div>
              </th>
            )}

            <th>{i18n.t('OrderFulfillmentPictures:picture')}</th>
            <th>{i18n.t('OrderFulfillmentPictures:takenAt')}</th>
            <th>{i18n.t('OrderFulfillmentPictures:location')}</th>
            <th>{i18n.t('OrderFulfillmentPictures:createdBy')}</th>
            <th>{i18n.t('OrderFulfillmentPictures:comment')}</th>
            {showDownload && <th>{i18n.t('Common:download')}</th>}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={7}>{i18n.t('Common:loading')}...</td>
            </tr>
          )}
          {error && (
            <tr>
              <td colSpan={7}>{i18n.t('Common:error')}</td>
            </tr>
          )}
          {!loading && !error && pictures.length === 0 && (
            <tr>
              <td colSpan={7}>{i18n.t('Common:noItems')}</td>
            </tr>
          )}
          {pictures.map((picture) => {
            const pic =
              pictureUpdates.find((p) => p.id === picture.id) ?? picture;
            return (
              <tr key={pic.id}>
                {!hideCheckbox && (
                  <td>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '160px',
                      }}
                    >
                      <Input
                        className="form-control"
                        type="checkbox"
                        checked={selectedPictureIds.includes(pic.id)}
                        onChange={() => handleCheckboxChange(pic.id)}
                      />
                    </div>
                  </td>
                )}
                <td>
                  <img
                    style={{
                      height: '10rem',
                      width: '10rem',
                      objectFit: 'cover',
                    }}
                    src={pic.url}
                    alt={pic.comment || undefined}
                    onClick={() => {
                      setSelectedImage(pic.url_full_size);
                      setShowImageFullscreen(!showImageFullscreen);
                    }}
                  />
                </td>
                <td>
                  {pic.taken_at != null
                    ? new Date(pic.taken_at).toLocaleDateString(i18n.language, {
                        ...getDateFormatOptions(true),
                        timeZone: 'UTC',
                      })
                    : '-'}
                </td>
                <td>
                  <div>
                    {pic.latitude != null && pic.longitude != null && (
                      <SimpleMap
                        latitude={pic.latitude}
                        longitude={pic.longitude}
                        size="10rem"
                      />
                    )}
                    {getAddressString(pic).length > 0
                      ? getAddressString(pic)
                      : '-'}
                  </div>
                </td>
                <td>
                  <Link to={'/users/' + pic.created_by_id}>
                    {pic.created_by_name}
                  </Link>
                </td>
                <td style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {picturesInEditMode.includes(pic.id) ? (
                    <>
                      <Input
                        type="textarea"
                        value={pic.comment || ''}
                        onChange={(e) => {
                          setPictureUpdates((prev) =>
                            prev.filter((p) => p.id === pic.id).length === 0
                              ? [...prev, { ...pic, comment: e.target.value }]
                              : prev.map((p) =>
                                  p.id === pic.id
                                    ? { ...p, comment: e.target.value }
                                    : p,
                                ),
                          );
                        }}
                      />

                      <Button onClick={() => onSave(pic)}>
                        <FaSave /> {i18n.t('Common:save')}
                      </Button>
                    </>
                  ) : (
                    <>
                      {pic.comment && <p>{pic.comment} </p>}

                      <Button
                        onClick={() =>
                          setPicturesInEditMode((prev) => [...prev, pic.id])
                        }
                      >
                        <FaEdit /> {i18n.t('Common:add')}
                      </Button>
                    </>
                  )}
                </td>
                {showDownload && (
                  <td>
                    <Button
                      onClick={() => {
                        downloadImage(
                          picture?.url_full_size || '',
                          `image.png`,
                        );
                      }}
                    >
                      <FaDownload /> {i18n.t('Common:download')}
                    </Button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
      <CustomPagination
        pageCount={pageCount}
        currentPage={page}
        onPageChange={setPage || (() => {})}
      />
      <Modal
        isOpen={showImageFullscreen}
        toggle={() => {
          setShowImageFullscreen(!showImageFullscreen);
        }}
        fullscreen
      >
        <ModalHeader
          toggle={() => {
            setShowImageFullscreen(!showImageFullscreen);
          }}
        ></ModalHeader>
        <ModalBody className="d-flex justify-content-center">
          <div className="image-zoomable-container">
            <img
              className="image-zoomable"
              src={selectedImage}
              alt=""
              onMouseMove={(e) => {
                const element = e.currentTarget;
                const { left, top, width, height } =
                  element.getBoundingClientRect();
                const x = (e.clientX - left) / width;
                const y = (e.clientY - top) / height;

                element.style.transformOrigin = `${x * 100}% ${y * 100}%`;
                element.style.transform = 'scale(2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
              }}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setShowImageFullscreen(!showImageFullscreen);
            }}
          >
            {i18n.t('Common:cancel')}
          </Button>
        </ModalFooter>
      </Modal>
      {showLinkButton && (
        <LinkOrderFulfillmentPicturesModal
          isOpen={linkModalIsOpen}
          setIsOpen={(newValue) => setLinkModalIsOpen(newValue)}
          pictureIds={selectedPictureIds}
          onComplete={loadCurrentPage}
        />
      )}
    </div>
  );
};
